import { useState, useEffect } from 'react';
import { getHistoryMovements, type Movement } from '../../../api/movements.api';
import { formatCOP } from '../../../utils/currencyFormat';

export default function RecentMovements() {
  const [movements, setMovements] = useState<Movement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMovements();
  }, []);

  const loadMovements = async () => {
    try {
      // Obtener solo movimientos recientes (el historial ya incluye activos)
      const history = await getHistoryMovements();
      
      // Ordenar por hora de entrada (más recientes primero) y limitar a 8
      const recent = history
        .sort((a, b) => new Date(b.horaEntrada).getTime() - new Date(a.horaEntrada).getTime())
        .slice(0, 8);
      
      setMovements(recent);
    } catch (error) {
      console.error('Error cargando movimientos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const getVehicleType = (tipo: string) => {
    const types: Record<string, string> = {
      'AUTO': 'Automóvil',
      'MOTO': 'Motocicleta',
      'CAMIONETA': 'Camioneta',
      'CAMION': 'Camión',
      'BICICLETA': 'Bicicleta'
    };
    return types[tipo] || tipo;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-12 bg-slate-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Movimientos Recientes</h2>
          <p className="text-sm text-slate-500 mt-1">Últimas entradas y salidas</p>
        </div>
      </div>

      {movements.length === 0 ? (
        <div className="py-8 text-center text-slate-400">
          <i className="ri-car-line text-4xl mb-2"></i>
          <p>No hay movimientos recientes</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Placa</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Vehículo</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Hora</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Operador</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Monto</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((movement) => (
                <tr key={movement.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                  <td className="py-4 px-4">
                    <span className="font-semibold text-slate-800">{movement.vehiculo.placa}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                      movement.tipoMovimiento === 'ENTRADA'
                        ? 'bg-green-50 text-green-600'
                        : 'bg-blue-50 text-blue-600'
                    }`}>
                      <i className={`${movement.tipoMovimiento === 'ENTRADA' ? 'ri-arrow-right-line' : 'ri-arrow-left-line'}`}></i>
                      {movement.tipoMovimiento === 'ENTRADA' ? 'Entrada' : 'Salida'}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-slate-600">{getVehicleType(movement.vehiculo.tipo)}</td>
                  <td className="py-4 px-4 text-sm text-slate-600">{formatTime(movement.horaEntrada)}</td>
                  <td className="py-4 px-4 text-sm text-slate-600">{movement.operador.nombre}</td>
                  <td className="py-4 px-4">
                    {movement.tarifa ? (
                      <span className="font-semibold text-slate-800">{formatCOP(movement.tarifa)}</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                      movement.status === 'ACTIVO'
                        ? 'bg-yellow-50 text-yellow-600'
                        : 'bg-teal-50 text-teal-600'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        movement.status === 'ACTIVO' ? 'bg-yellow-600' : 'bg-teal-600'
                      }`}></span>
                      {movement.status === 'ACTIVO' ? 'Activo' : 'Completado'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
